import { AllFormData } from '../types';
import { formatPhoneNumberWithCountryCode } from '../utils';

const HUBSPOT_PORTAL_ID = '50110428';
// In hubspot, the demo and signup flows each have a form. When we submit through this
// endpoint, the form is filled with out values and submitted, which is why we need to
// include the form ids in the url.
const HUBSPOT_DEMO_FORM_ID = '5e27b580-f17f-40a8-9e76-878ce312dc8a';
const HUBSPOT_SIGNUP_FORM_ID = '7a94d8bc-2ecf-4eff-988b-f4591f053b36';

type HubspotFormType = 'signup' | 'demo';

const getHubspotEndpoint = (formType: HubspotFormType) => {
  const formId = formType === 'demo' ? HUBSPOT_DEMO_FORM_ID : HUBSPOT_SIGNUP_FORM_ID;

  if (!HUBSPOT_PORTAL_ID || !formId) {
    return null;
  }

  return `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${formId}`;
};

type HubspotFieldValue = string | number | boolean | undefined | null;

type HubspotSignupData = Partial<
  Pick<
    AllFormData,
    | 'affiliation'
    | 'contact_phone'
    | 'email'
    | 'ehr'
    | 'first_name'
    | 'heard_about_from'
    | 'last_name'
    | 'organization_type'
    | 'practice_name'
    | 'practice_website'
    | 'role'
    | 'size_numeric'
    | 'source'
    | 'specialty'
    | 'specify_other_ehr'
    | 'specify_other_heard_about_from'
    | 'specify_other_specialty'
    | 'utm_campaign'
    | 'utm_content'
    | 'utm_medium'
    | 'utm_source'
    | 'utm_term'
  >
> & { booked_demo?: boolean; google_ad_click_id?: string };

// HubSpot field schema lives in python/practice/hubspot_schema.py
// Contact fields (objectTypeId: '0-1')
// There is also an objectTypeId: '0-2' for company fields, but we don't use them here.
const HUBSPOT_CONTACT_FIELD_MAP: Record<string, string> = {
  email: 'email',
  first_name: 'firstname',
  last_name: 'lastname',
  contact_phone: 'contact_phone',
  practice_name: 'company',
  practice_website: 'practice_website',
  affiliation: 'affiliation',
  specialty: 'specialty',
  specify_other_specialty: 'specialty_free_form',
  size_numeric: 'size_numeric',
  organization_type: 'organization_type',
  ehr: 'ehr',
  specify_other_ehr: 'ehr_free_form',
  heard_about_from: 'heard_about_from',
  specify_other_heard_about_from: 'heard_about_from_free_form',
  role: 'jobtitle',
  source: 'lead_source',
  utm_source: 'utm_source',
  utm_medium: 'utm_medium',
  utm_campaign: 'utm_campaign',
  utm_term: 'utm_term',
  utm_content: 'utm_content',
  booked_demo: 'booked_demo',
  google_ad_click_id: 'google_ad_click_id',
};

const formatFields = (data?: HubspotSignupData) => {
  if (!data) return [];

  return Object.entries(HUBSPOT_CONTACT_FIELD_MAP).flatMap(([key, hubspotField]) => {
    const value = data[key as keyof HubspotSignupData] as HubspotFieldValue;

    if (value === undefined || value === null || value === '') {
      return [];
    }

    const formattedValue = typeof value === 'boolean' || typeof value === 'number' ? String(value) : value;
    return [{ objectTypeId: '0-1', name: hubspotField, value: formattedValue }];
  });
};

/**
 * Submit demo and signup data to HubSpot and push conversion data to Google Tag Manager.
 *
 * As the user navigates through the flows, we submit the data that we have at that time in case
 * the user drops off before completing the flow. This produces multiple submissions for the same user,
 * but those submissions are deduplicated by HubSpot using the email as the key.
 * Later, a cron job in hubspot_cron.py will push up the data we recorded in the database to HubSpot,
 * which will be combined with the data we submitted here.
 *
 * Google Enhanced Conversions:
 * - Data is pushed to window.dataLayer for GTM to consume
 * - Multiple submissions with overlapping data are acceptable (Google's cookies stitch the journey)
 * - gclid (Google Click ID) is extracted from URL params for ad attribution
 * - User data (email, phone) is sent in Enhanced Conversions format
 *
 * Verification:
 * - Use Google Tag Assistant (https://tagassistant.google.com/) to verify data flow in staging
 * - GTM doesn't store PII - Tag Assistant shows real-time data routing
 *
 * @param formType - 'signup' or 'demo' flow
 * @param data - Form data including email, phone, and HubSpot fields
 */
export const submitHubspotForm = async (formType: HubspotFormType, data?: HubspotSignupData) => {
  const endpoint = getHubspotEndpoint(formType);

  if (!endpoint) {
    console.warn('HubSpot portal or form ID not configured; skipping HubSpot submission.');
    return;
  }

  // Use gclid from data if available (passed from formState), otherwise fall back to URL
  // This ensures gclid persists across navigation steps even when URL params are lost
  const gclid = data?.google_ad_click_id ?? new URLSearchParams(window.location.search).get('gclid') ?? undefined;
  const fields = formatFields({ ...data, google_ad_click_id: gclid });

  // We need at least an email for contact submission
  if (!fields.some((field) => field.name === 'email')) {
    console.warn('HubSpot submission skipped because no email was provided.');
    return;
  }

  // Format phone number with +1 country code for US numbers (E.164 format)
  // contact_phone is the user's contact number (collected in ProfileInfo step)
  const phoneWithCountryCode = formatPhoneNumberWithCountryCode(data?.contact_phone);

  // User data for Google Enhanced Conversions
  // Format matches Google's requirements: email (hashed by GTM) and phone_number (E.164 format)
  const leadsUserData = {
    email: data?.email,
    phone_number: phoneWithCountryCode,
  };

  // Push conversion data to Google Tag Manager for Enhanced Conversions
  // Only push if we have a gclid (user came from a Google Ad) to avoid sending PII unnecessarily
  // - event: Triggers GTM tags configured for 'formSubmitted'
  // - leadsUserData: User PII for Enhanced Conversions (email, phone)
  // - gclid: Google Click ID for ad attribution (top-level for GTM access)
  //
  // Note: Multiple form submissions (email-only, then email+phone) are acceptable.
  // Google uses browser cookies (_ga, _gcl_aw) to stitch the user journey together,
  // so duplicate data across submissions is recognized as the same person.
  if (gclid) {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      event: 'formSubmitted',
      leadsUserData,
      gclid,
    });
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      fields,
      context: {
        pageUri: window.location.href,
        pageName: document.title,
      },
    }),
  });

  if (!response.ok) {
    const error = new Error(`HubSpot submission returned a non-OK response: ${response.statusText}`);
    console.error('HubSpot submission returned a non-OK response', response.statusText);
    throw error;
    // It's intentional that we don't retry or pop an error toast.
  }
};
